import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';

interface NewExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    amount: number;
    taxDeductible: number;
    date: string;
    dueDate?: string;
    refNo?: string;
    isCost?: boolean;
  }) => void;
  loading?: boolean;
}

interface ExpenseFormValues {
  title: string;
  amount: string;
  taxDeductible: string;
  date: string;
  dueDate: string;
  refNo: string;
  isCost: boolean;
}

export function NewExpenseDialog({ open, onClose, onSubmit, loading = false }: NewExpenseDialogProps) {
  const today = new Date().toISOString().substring(0, 10);
  const defaultDueDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);

  const taxDeductibleEdited = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ExpenseFormValues>({
    defaultValues: {
      title: '',
      amount: '',
      taxDeductible: '',
      date: today,
      dueDate: defaultDueDate,
      refNo: '',
      isCost: false,
    },
  });

  useEffect(() => {
    if (open) {
      taxDeductibleEdited.current = false;
      reset({
        title: '',
        amount: '',
        taxDeductible: '',
        date: new Date().toISOString().substring(0, 10),
        dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10),
        refNo: '',
        isCost: false,
      });
    }
  }, [open, reset]);

  const amountValue = watch('amount');

  useEffect(() => {
    if (!taxDeductibleEdited.current) {
      setValue('taxDeductible', amountValue);
    }
  }, [amountValue, setValue]);

  const onFormSubmit = (values: ExpenseFormValues) => {
    const parsedAmount = parseFloat(values.amount);
    const parsedTaxDeductible = parseFloat(values.taxDeductible);
    const effectiveTaxDeductible = !isNaN(parsedTaxDeductible) ? parsedTaxDeductible : parsedAmount;

    onSubmit({
      title: values.title.trim(),
      amount: parsedAmount,
      taxDeductible: effectiveTaxDeductible,
      date: new Date(values.date).toISOString(),
      dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : undefined,
      refNo: values.refNo.trim() || undefined,
      isCost: values.isCost,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogTitle>Nový výdavok</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Názov *"
                {...register('title', { validate: (v) => !!v.trim() || 'Názov je povinný' })}
                error={!!errors.title}
                helperText={errors.title?.message ?? ''}
                fullWidth
                variant="standard"
                disabled={loading}
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Suma (€) *"
                type="number"
                {...register('amount', {
                  required: 'Zadajte platnú sumu',
                  validate: (v) => (parseFloat(v) > 0) || 'Zadajte platnú sumu',
                })}
                error={!!errors.amount}
                helperText={errors.amount?.message ?? ''}
                fullWidth
                variant="standard"
                disabled={loading}
                inputProps={{ step: '0.01', min: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Odpočítateľné z dane (€)"
                type="number"
                {...register('taxDeductible', {
                  onChange: () => {
                    taxDeductibleEdited.current = true;
                  },
                })}
                fullWidth
                variant="standard"
                disabled={loading}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Predvolene = celá suma"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Dátum výdavku *"
                type="date"
                {...register('date', { required: true })}
                fullWidth
                variant="standard"
                disabled={loading}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Dátum splatnosti"
                type="date"
                {...register('dueDate')}
                fullWidth
                variant="standard"
                disabled={loading}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Referenčné číslo"
                {...register('refNo')}
                fullWidth
                variant="standard"
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="isCost"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Náklad (okamžite zaplatený)"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Vytvoriť
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
